import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Menu, X, Search } from 'lucide-react';
import './Header.css';
import farmerAvatar from './farmer-avatar.png';
import { CollapsibleSearch } from '../molecules/CollapsibleSearch';

export interface NavItem {
  label: string;
  href: string;
  /** Optional click handler - when provided, prevents default navigation */
  onClick?: () => void;
}

export interface UserInfo {
  name: string;
  email?: string;
  avatarUrl?: string;
}

export interface HeaderProps {
  /** Logo image source */
  logoSrc?: string;
  /** Logo link target (defaults to '/') */
  logoHref?: string;
  /** Brand name displayed next to logo */
  brandName?: string;
  /** Navigation items for desktop menu */
  navItems?: NavItem[];
  /** User information */
  user?: UserInfo;
  /** Handler for logout action */
  onLogout?: () => void;
  /** Handler for profile link */
  onProfileClick?: () => void;
  /** Handler for settings link */
  onSettingsClick?: () => void;
  /** Handler for integrations link */
  onIntegrationsClick?: () => void;
  /** Custom className for styling */
  className?: string;
  /** Whether to show collapsible search */
  showSearch?: boolean;
  /** Current search query */
  searchQuery?: string;
  /** Search query change handler */
  onSearchChange?: (query: string) => void;
  /** Search placeholder text (shown when expanded) */
  searchPlaceholder?: string;
  /** Search collapsed label (shown when collapsed) */
  searchCollapsedLabel?: string;
  /** Use compact height (48px instead of 64px) */
  compact?: boolean;
  /** Label for profile menu item */
  profileLabel?: string;
  /** Label for settings menu item */
  settingsLabel?: string;
  /** Label for integrations menu item */
  integrationsLabel?: string;
  /** Label for sign out menu item */
  signOutLabel?: string;
  /** Login link href (shown when no user is provided) */
  loginHref?: string;
  /** Login button label */
  loginLabel?: string;
  /** Mobile: handler for side panel menu button (shown left of logo) */
  onMobileSideMenuClick?: () => void;
  /** Mobile: page title to show after logo */
  mobilePageTitle?: string;
}

// Default avatar - farmer character
const DEFAULT_AVATAR = farmerAvatar;

/**
 * Header component with responsive navigation, user menu, and mobile support
 */
export const Header: React.FC<HeaderProps> = ({
  logoSrc,
  logoHref = '/',
  brandName = 'Hofsync',
  navItems = [],
  user,
  onLogout,
  onProfileClick,
  onSettingsClick,
  onIntegrationsClick,
  className = '',
  showSearch = false,
  searchQuery = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchCollapsedLabel = 'Search...',
  compact = false,
  profileLabel = 'Your Profile',
  settingsLabel = 'Settings',
  integrationsLabel = 'Integrations',
  signOutLabel = 'Sign out',
  loginHref,
  loginLabel = 'Log in',
  onMobileSideMenuClick,
  mobilePageTitle,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileProfileMenuOpen, setMobileProfileMenuOpen] = useState(false);
  const [internalSearchQuery, setInternalSearchQuery] = useState(searchQuery);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const mobileProfileMenuRef = useRef<HTMLDivElement | null>(null);

  // Sync internal search state with prop
  useEffect(() => {
    setInternalSearchQuery(searchQuery);
  }, [searchQuery]);

  const handleSearchChange = (value: string) => {
    setInternalSearchQuery(value);
    onSearchChange?.(value);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    // Close profile menu when opening search
    if (!mobileMenuOpen) {
      setMobileProfileMenuOpen(false);
    }
  };

  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
      if (
        mobileProfileMenuRef.current &&
        !mobileProfileMenuRef.current.contains(event.target as Node)
      ) {
        setMobileProfileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const avatarUrl = user?.avatarUrl || DEFAULT_AVATAR;

  return (
    <header className={`header ${compact ? 'header--compact' : ''} ${className}`}>
      <div className="header-container">
        <div className={`header-content ${compact ? 'header-content--compact' : ''}`}>
          {/* Mobile side menu button (for LensePanel) - shown left of logo */}
          {onMobileSideMenuClick && (
            <button
              type="button"
              className="header-mobile-side-menu"
              onClick={onMobileSideMenuClick}
              aria-label="Open categories menu"
            >
              <Menu className="header-icon" aria-hidden="true" />
            </button>
          )}

          {/* Logo & Brand Name */}
          <div className="header-brand">
            <a href={logoHref} className="header-brand-link">
              {logoSrc && (
                <img
                  src={logoSrc}
                  alt={`${brandName} Logo`}
                  className="header-logo"
                />
              )}
              <span className="header-brand-text">{brandName}</span>
            </a>
          </div>

          {/* Mobile page title - shown after logo on mobile */}
          {mobilePageTitle && (
            <span className="header-mobile-page-title">{mobilePageTitle}</span>
          )}

          {/* Center: Collapsible Search (when enabled) or Desktop Navigation */}
          {showSearch ? (
            <div className="header-search">
              <CollapsibleSearch
                value={internalSearchQuery}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                collapsedLabel={searchCollapsedLabel}
              />
            </div>
          ) : (
            /* Desktop Navigation styled as folder bookmarks */
            <nav className="header-nav">
              {navItems.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  className={`header-nav-tab ${
                    index === 0 ? 'header-nav-tab--active' : ''
                  }`}
                >
                  {item.label}
                </a>
              ))}
              {/* bottom line to create folder edge */}
              <div className="header-nav-line" />
            </nav>
          )}

          {/* User and Mobile Menu Controls */}
          <div className="header-actions">
            {user ? (
              /* User Menu */
              <div className="header-user-menu" ref={userMenuRef}>
                <button
                  className="header-user-button"
                  onClick={toggleUserMenu}
                  aria-label="User menu"
                  aria-expanded={userMenuOpen}
                >
                  <span className="header-user-name">{user.name}</span>
                  <ImageWithFallback
                    src={avatarUrl}
                    alt={user.name}
                    className="header-user-avatar"
                  />
                  <ChevronDown
                    className={`header-user-chevron ${
                      userMenuOpen ? 'header-user-chevron--open' : ''
                    }`}
                  />
                </button>

                {userMenuOpen && (
                  <div className="header-user-dropdown">
                    {onProfileClick && (
                      <button
                        className="header-user-dropdown-item"
                        onClick={() => {
                          setUserMenuOpen(false);
                          onProfileClick();
                        }}
                      >
                        {profileLabel}
                      </button>
                    )}
                    {onSettingsClick && (
                      <button
                        className="header-user-dropdown-item"
                        onClick={() => {
                          setUserMenuOpen(false);
                          onSettingsClick();
                        }}
                      >
                        {settingsLabel}
                      </button>
                    )}
                    {onIntegrationsClick && (
                      <button
                        className="header-user-dropdown-item"
                        onClick={() => {
                          setUserMenuOpen(false);
                          onIntegrationsClick();
                        }}
                      >
                        {integrationsLabel}
                      </button>
                    )}
                    {onLogout && (
                      <button
                        className="header-user-dropdown-item"
                        onClick={() => {
                          setUserMenuOpen(false);
                          onLogout();
                        }}
                      >
                        {signOutLabel}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : loginHref ? (
              /* Login Link */
              <a href={loginHref} className="header-login">
                {loginLabel}
              </a>
            ) : null}

            {/* Mobile search button - only shown when showSearch is true */}
            {showSearch && (
              <div className="header-mobile-menu-button">
                <button
                  type="button"
                  className="header-mobile-toggle"
                  onClick={toggleMobileMenu}
                  aria-label={mobileMenuOpen ? 'Close search' : 'Open search'}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? (
                    <X className="header-icon" aria-hidden="true" />
                  ) : (
                    <Search className="header-icon" aria-hidden="true" />
                  )}
                </button>
              </div>
            )}

            {/* Mobile profile button with dropdown */}
            {user && (
              <div className="header-mobile-profile-wrapper" ref={mobileProfileMenuRef}>
                <button
                  type="button"
                  className="header-mobile-profile-button"
                  onClick={() => {
                    const newState = !mobileProfileMenuOpen;
                    setMobileProfileMenuOpen(newState);
                    // Close search menu when opening profile
                    if (newState) {
                      setMobileMenuOpen(false);
                    }
                  }}
                  aria-label="Open profile menu"
                  aria-expanded={mobileProfileMenuOpen}
                >
                  <ImageWithFallback
                    src={avatarUrl}
                    alt={user.name}
                    className="header-mobile-profile-avatar"
                  />
                </button>

                {/* Mobile profile dropdown */}
                {mobileProfileMenuOpen && (
                  <div className="header-mobile-profile-dropdown">
                    {/* Navigation items section */}
                    {navItems.length > 0 && (
                      <div className="header-mobile-profile-nav">
                        {navItems.map((item, index) => (
                          <button
                            key={index}
                            className="header-mobile-profile-nav-item"
                            onClick={() => {
                              setMobileProfileMenuOpen(false);
                              item.onClick?.();
                            }}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Divider */}
                    {navItems.length > 0 && (onSettingsClick || onLogout) && (
                      <div className="header-mobile-profile-divider" />
                    )}

                    {/* Profile actions section */}
                    <div className="header-mobile-profile-actions">
                      {onSettingsClick && (
                        <button
                          className="header-mobile-profile-action"
                          onClick={() => {
                            setMobileProfileMenuOpen(false);
                            onSettingsClick();
                          }}
                        >
                          {settingsLabel}
                        </button>
                      )}
                      {onIntegrationsClick && (
                        <button
                          className="header-mobile-profile-action"
                          onClick={() => {
                            setMobileProfileMenuOpen(false);
                            onIntegrationsClick();
                          }}
                        >
                          {integrationsLabel}
                        </button>
                      )}
                      {onLogout && (
                        <button
                          className="header-mobile-profile-action"
                          onClick={() => {
                            setMobileProfileMenuOpen(false);
                            onLogout();
                          }}
                        >
                          {signOutLabel}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile search dropdown - only search box, no nav or user items */}
      {showSearch && (
        <div
          className={`header-mobile-menu ${
            mobileMenuOpen ? 'header-mobile-menu--open' : ''
          }`}
        >
          <div className="header-mobile-search">
            <div className="header-mobile-search-wrapper">
              <Search className="header-mobile-search-icon" />
              <input
                type="text"
                className="header-mobile-search-input"
                placeholder={searchPlaceholder}
                value={internalSearchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
              {internalSearchQuery && (
                <button
                  className="header-mobile-search-clear"
                  onClick={() => handleSearchChange('')}
                  aria-label="Clear search"
                >
                  <X className="header-mobile-search-clear-icon" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// ImageWithFallback component for robust image loading
interface ImageWithFallbackProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {}

function ImageWithFallback(props: ImageWithFallbackProps) {
  const [didError, setDidError] = useState(false);
  const { src, alt, style, className, ...rest } = props;

  return didError ? (
    <div
      className={`inline-block bg-gray-100 text-center align-middle ${
        className ?? ''
      }`}
      style={style}
    >
      <div className="flex items-center justify-center w-full h-full">
        <svg
          width="100%"
          height="100%"
          xmlns="http://www.w3.org/2000/svg"
          stroke="#000"
          strokeLinejoin="round"
          opacity=".3"
          fill="none"
          strokeWidth="3.7"
          viewBox="0 0 88 88"
        >
          <rect x="16" y="16" width="56" height="56" rx="6" />
          <path d="m16 58 16-18 32 32" />
          <circle cx="53" cy="35" r="7" />
        </svg>
      </div>
    </div>
  ) : (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      {...rest}
      onError={() => setDidError(true)}
    />
  );
}
